import get from 'lodash/get';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { Portal } from '@spothero/ui-backlog';
import { Button } from '@spothero/ui';
import GTMUtils from 'utils/gtm';
import SHLoader from 'common/loader';
import { fetchRates as fetchAirportRates } from 'store/checkout/checkout-actions';
import { push as pushTo, goBack as goBackToPreviousPage, } from 'store/router/router-actions';
import trackAvailabilityCheck from 'segment/events/availability-check';
import trackSpotDetails from 'segment/pages/spot-details';
import SHUtils from 'utils/sh-utils';
import SpotImages from 'common/spot-details/spot-images';
import BenefitMessage from './components/benefit-message';
import AirportPurchaseInformation from './components/purchase-information-desktop-sidebar';
import AirportPurchaseSnapshot from './components/purchase-information-mobile-snapshot';
import IconArrowLeft from '@spothero/icons/arrow-left';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import DateTimeRangeModal from 'pages/search-airport/components/search-results/DateTimeRangeModal';
import { CarouselVariant } from 'common/image-carousel';
import { GoogleMapsProvider } from 'common/google-map-context';
import SpotDetailPriceBreakdownModal from 'common/spot-details/price-breakdown-modal/SpotDetailPriceBreakdownModal';
import Config from '@/config/index';
import AirportDetails from './components/airport-details';
const SpotDetailsAirport = ({ match: { params: { spotId }, }, }) => {
    const isMobile = Config.isMobile;
    const apiDateTimeFormat = Config.apiDateTimeFormat;
    const dispatch = useDispatch();
    const [showPriceBreakdownModal, setShowPriceBreakdownModal] = useState(false);
    const [showConfirmTimeModal, setShowConfirmTimeModal] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [confirmTimeModalEditMode, setConfirmTimeModalEditMode] = useState(false);
    const [
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    confirmTimeModalErrorMessage, setConfirmTimeModalErrorMessage,] = useState(null);
    const [start, setStart] = useState(null);
    const [end, setEnd] = useState(null);
    const [isValidStart, setIsValidStart] = useState(true);
    const storeState = useSelector((state) => state);
    const { city: { data: city }, destination: { data: destination }, checkout: { rateSessionId, currencyType, isFetchingRates: isLoading }, search: { data: { referrerAffiliate }, }, searchRequest: { monthly, starts, ends, referrer }, spot: { selected: spot }, user: { data: { status: userStatus }, }, } = storeState;
    let lastActionType = 'SEARCH';
    const bodyClasses = SHUtils.determineInitialLoadBodyClasses({
        isAirport: true,
        pageType: 'facility',
    });
    const facility = spot?.facility || null;
    const selectedRate = spot?.selectedRate || null;
    const linearMeters = spot?.distance || null;
    const comparisons = spot?.comparisons || null;
    useEffect(() => {
        dispatch(fetchAirportRates({
            spotId: parseInt(spotId, 10),
            starts,
            ends,
            airport: true,
            eventPackageId: null,
            includeFacility: true,
            meta: {
                dateTimeChanged: true,
            },
        }));
        GTMUtils.push({
            event: 'pageview',
            page: 'spotdetail-airport',
        });
        SHUtils.staticScrollTo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (spot) {
            // @ts-expect-error - temporary unitl trackspotdetails is typed
            trackSpotDetails({
                city,
                destination,
                isMonthly: monthly,
                starts,
                ends,
                spot,
                referrer,
                referrerAffiliate,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [spot]);
    useEffect(() => {
        // a new rate load was initiated which signifies an availability check
        if (spot) {
            trackAvailabilityCheck({
                searchRequest: {
                    monthly,
                    starts,
                    ends,
                },
                city,
                spot,
                userStatus,
                screen: 'spot details',
                actionType: lastActionType,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rateSessionId]);
    const onApplyDateTimeChange = (newStart, newEnd) => {
        const { facility: { id: spotIdFromSpot }, } = spot;
        setShowConfirmTimeModal(false);
        setConfirmTimeModalEditMode(false);
        setConfirmTimeModalErrorMessage(null);
        lastActionType = 'UPDATE_REQUESTED_TIMES';
        dispatch(fetchAirportRates({
            spotId: spotIdFromSpot,
            starts: newStart,
            ends: newEnd,
            airport: true,
            eventPackageId: null,
            includeFacility: true,
            meta: {
                dateTimeChanged: true,
            },
        }));
    };
    const onHideConfirmDateTimeModal = () => {
        setShowConfirmTimeModal(false);
        setConfirmTimeModalEditMode(false);
        setConfirmTimeModalErrorMessage(null);
    };
    const onShowConfirmDateTimeModal = isEditMode => {
        setShowConfirmTimeModal(true);
        setConfirmTimeModalEditMode(isEditMode || false);
    };
    const onShowPriceBreakdownModal = () => {
        setShowPriceBreakdownModal(true);
    };
    const onHidePriceBreakdownModal = () => {
        setShowPriceBreakdownModal(false);
    };
    const onCheckoutClick = () => {
        const { selectedRate: { url }, } = spot;
        dispatch(pushTo(url));
    };
    const onHandleBackClick = () => {
        dispatch(goBackToPreviousPage());
    };
    const onDateTimeChange = ({ start: updatedStart, end: updatedEnd, isValidStart: isValidUpdatedStart, }) => {
        setIsValidStart(isValidUpdatedStart);
        if (!isValidUpdatedStart) {
            return;
        }
        setStart(updatedStart.format(apiDateTimeFormat));
        setEnd(updatedEnd.format(apiDateTimeFormat));
    };
    const onUpdateDateTime = () => {
        if (!start || !end) {
            onApplyDateTimeChange(starts, ends);
            return;
        }
        const updatedStart = start || starts;
        const updatedEnd = end || ends;
        onApplyDateTimeChange(updatedStart, updatedEnd);
    };
    if (isLoading || !facility) {
        return <SHLoader className="SpotDetailsAirport-loader"/>;
    }
    const { airport, cancellation_allowed: cancellationAllowed, amenities_full: amenities, rating_info: ratingInfo, images, title, available_transient_inventory: availableTransientInventory, restrictions: facilityRestrictions, } = facility;
    const { details: { code: iataCode }, shuttle, lowest_daily_rate: lowestDailyRate, } = airport;
    const totalPrice = selectedRate?.price_breakdown?.total_price
        ? selectedRate.price_breakdown.total_price
        : null;
    const displayPrice = selectedRate
        ? totalPrice
            ? totalPrice
            : selectedRate.display_price
        : lowestDailyRate;
    const hasInOutAmenity = Boolean(amenities.find(amenity => amenity.slug === 'in-out'));
    const { star_rating: starRating } = ratingInfo;
    const airportPercentLowerPrice = get(comparisons, 'airport_percent_lower_price', 0);
    const showAirportPercentLowerMessage = airportPercentLowerPrice >= 40 && starRating > 4;
    const showBenefitMessage = !isEmpty(comparisons) && showAirportPercentLowerMessage;
    const backButtonClasses = classNames('BackButton', {
        showBelowBanner: showBenefitMessage,
        noBannerBackButton: !showBenefitMessage,
    });
    return (<GoogleMapsProvider>
            <div className="SpotDetailsAirport">
                {/* @ts-expect-error */}
                <Helmet>
                    <meta name="robots" content="noindex, follow"/>
                    <body className={bodyClasses}/>
                </Helmet>
                <div className="SpotDetailsAirport-main-content">
                    {isMobile && (<Button _hover={{ backgroundColor: 'transparent' }} backgroundColor="transparent" borderWidth={0} className={backButtonClasses} onClick={onHandleBackClick}>
                            {/* span allows CSS to create larger tap target for this button */}
                            <span>
                                <IconArrowLeft />
                            </span>
                        </Button>)}
                    {showBenefitMessage && (<BenefitMessage percentLowerPrice={airportPercentLowerPrice} iataCode={iataCode}/>)}
                    <SpotImages images={images} alt={title} carouselVariant={CarouselVariant.SINGLE}/>
                    <AirportDetails shuttle={shuttle} title={title} iataCode={iataCode} facility={facility} selectedRate={selectedRate} linearMeters={linearMeters} amenities={amenities} hasInOutAmenity={hasInOutAmenity} facilityRestrictions={facilityRestrictions} cancellationAllowed={cancellationAllowed} airportPercentLowerPrice={airportPercentLowerPrice} showBenefitMessage={showBenefitMessage}/>
                </div>
                {isMobile ? (<Portal>
                        <AirportPurchaseSnapshot cancellationAllowed={cancellationAllowed} availableTransientInventory={availableTransientInventory} displayPrice={displayPrice} currencyType={currencyType} checkoutDisabled={!selectedRate} onCheckoutClick={onCheckoutClick} onShowPriceBreakdownModal={onShowPriceBreakdownModal}/>
                    </Portal>) : (<div className="SpotDetailsAirport-checkout">
                        <AirportPurchaseInformation onCheckoutClick={onCheckoutClick} facility={facility} selectedRate={selectedRate} starts={starts} ends={ends} availableTransientInventory={availableTransientInventory} isFetchingRates={isLoading} displayPrice={displayPrice} currencyType={currencyType} onShowConfirmDateTimeModal={onShowConfirmDateTimeModal} onShowPriceBreakdownModal={onShowPriceBreakdownModal}/>
                    </div>)}
                {showConfirmTimeModal && (<DateTimeRangeModal submitDisabled={!isValidStart} onUpdateDateTime={onUpdateDateTime} onDateTimeChange={onDateTimeChange} onModalClose={onHideConfirmDateTimeModal} modalTitle="Change Reservation Date and Time"/>)}
                {showPriceBreakdownModal && (<SpotDetailPriceBreakdownModal spot={spot} onHidden={onHidePriceBreakdownModal}/>)}
            </div>
        </GoogleMapsProvider>);
};
export default SpotDetailsAirport;
